import { useEffect, useRef, useState } from 'react';
import { Loader2, Copy, Check, CheckCircle2 } from 'lucide-react';
import BookingResultView, { type BookingResultResponse } from '../components/BookingResultView';
import { normalizeBookingResult } from '../lib/normalizeBookingResult';
import { applyMultipleGutscheine, type AppliedGutschein } from '../lib/gutschein';

interface PaymentStatus {
  status: 'pending' | 'succeeded' | 'failed' | 'refunded' | 'conflict_refunded';
  reference_type: 'booking' | 'gutschein';
  reference_id: string;
  amount_eur: number;
  metadata?: {
    gutschein_code?: string;
    buyer_name?: string;
    buyer_email?: string;
    bookingConfirmed?: boolean;
    paymentRefunded?: boolean;
    bookingResult?: BookingResultResponse;
    [key: string]: unknown;
  };
}

export default function PaymentSuccessPage() {
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [conflictResult, setConflictResult] = useState<BookingResultResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const gutscheinRedeemed = useRef(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sessionId = p.get('session_id');
    const paypalToken = p.get('token');
    const paypalPayerId = p.get('PayerID');

    if (paypalToken && paypalPayerId) {
      handlePayPalSuccess(paypalToken, paypalPayerId);
    } else if (sessionId) {
      checkStripePaymentStatus(sessionId);
    } else {
      setConflictResult(
        normalizeBookingResult(null, { paymentCharged: false })
      );
      setLoading(false);
    }
  }, []);

  // Redeem gutscheine stored in sessionStorage once Stripe booking succeeds
  useEffect(() => {
    if (
      !paymentStatus ||
      paymentStatus.status !== 'succeeded' ||
      paymentStatus.reference_type !== 'booking' ||
      paymentStatus.metadata?.bookingConfirmed === false ||
      gutscheinRedeemed.current
    ) return;

    const raw = sessionStorage.getItem('pendingGutscheine');
    if (!raw) return;

    gutscheinRedeemed.current = true;
    sessionStorage.removeItem('pendingGutscheine');

    try {
      const { gutscheine, subtotal, customerEmail } = JSON.parse(raw);
      if (!gutscheine?.length) return;
      applyMultipleGutscheine(gutscheine as AppliedGutschein[], subtotal ?? paymentStatus.amount_eur, customerEmail ?? paymentStatus.reference_id).catch((err) =>
        console.error('[PaymentSuccessPage] gutschein redemption failed:', err)
      );
    } catch (err) {
      console.error('[PaymentSuccessPage] failed to parse pendingGutscheine:', err);
    }
  }, [paymentStatus]);

  const handlePayPalSuccess = async (token: string, payerId: string) => {
    try {
      const response = await fetch('https://n8n.cogniiq.co/webhook/gutschein-paypal-capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, PayerID: payerId }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.error('PayPal capture error:', response.status, data);
        setConflictResult(
          normalizeBookingResult(data, {
            httpStatus: response.status,
            paymentCharged: true,
          })
        );
        setLoading(false);
        return;
      }

      setPaymentStatus({
        status: 'succeeded',
        reference_type: 'gutschein',
        reference_id: data.order_id || token,
        amount_eur: data.amount || 0,
        metadata: {
          gutschein_code: data.gutschein_code,
          buyer_name: data.buyer_name,
          buyer_email: data.buyer_email,
        },
      });
      setLoading(false);
    } catch (err) {
      console.error('PayPal capture network error:', err);
      setConflictResult(normalizeBookingResult(null, { paymentCharged: true }));
      setLoading(false);
    }
  };

  const checkStripePaymentStatus = async (sessionId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-payment-status`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ session_id: sessionId }),
        }
      );

      if (!response.ok) {
        console.error('get-payment-status error:', response.status);
        setConflictResult(normalizeBookingResult(null, { paymentCharged: true }));
        setLoading(false);
        return;
      }

      const data: PaymentStatus = await response.json();

      // Retry while still pending
      if (data.status === 'pending') {
        setTimeout(() => checkStripePaymentStatus(sessionId), 2000);
        return;
      }

      // Refunded or conflict-refunded → conflict screen
      if (data.status === 'refunded' || data.status === 'conflict_refunded') {
        const meta = data.metadata;
        setConflictResult(
          normalizeBookingResult(
            meta?.bookingResult ?? { success: false, bookingConfirmed: false },
            { paymentCharged: true, paymentRefunded: true }
          )
        );
        setLoading(false);
        return;
      }

      setPaymentStatus(data);
      setLoading(false);
    } catch (err) {
      console.error('Stripe status check network error:', err);
      setConflictResult(normalizeBookingResult(null, { paymentCharged: true }));
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    if (paymentStatus?.metadata?.gutschein_code) {
      try {
        await navigator.clipboard.writeText(paymentStatus.metadata.gutschein_code as string);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // ignore
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
        <div className="w-full max-w-[560px]">
          <div className="text-center mb-8">
            <img
              src="/photos/Image.jpeg"
              alt="SV Heinersreuth"
              className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
            />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
              SV Heinersreuth
            </p>
            <p className="svh-label mt-1">Padel &amp; Tennis</p>
          </div>

          <div className="svh-card svh-card-lg svh-card-raised overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
            <div className="p-8 md:p-10">
              <div className="flex flex-col items-center justify-center py-6 gap-5 text-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--svh-crest-wash)' }}
                >
                  <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--svh-crest)' }} strokeWidth={2} />
                </div>
                <div>
                  <h1 className="svh-h3 mb-1.5">Zahlung wird bestätigt ...</h1>
                  <p className="svh-meta">Bitte warten Sie einen Moment.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
        </div>
      </div>
    );
  }

  // Any conflict / error / unknown → use BookingResultView
  if (conflictResult) {
    return (
      <BookingResultView
        result={conflictResult}
        autoRedirect={false}
        redirectUrl="/"
        onNavigateBooking={() => { window.location.href = '/buchen'; }}
      />
    );
  }

  // ── Gutschein purchase success (PayPal or Stripe) ──────────────────────────
  if (paymentStatus?.status === 'succeeded' && paymentStatus.reference_type === 'gutschein') {
    const gutscheinCode = paymentStatus.metadata?.gutschein_code as string | undefined;

    return (
      <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
        <div className="w-full max-w-[560px]">
          <div className="text-center mb-8">
            <img
              src="/photos/Image.jpeg"
              alt="SV Heinersreuth"
              className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
            />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
              SV Heinersreuth
            </p>
            <p className="svh-label mt-1">Padel &amp; Tennis</p>
          </div>

          <div className="svh-card svh-card-lg svh-card-raised overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
            <div className="p-8 md:p-10">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={1.75} />
                </div>
              </div>

              <div className="text-center mb-8">
                <p className="text-[12px] font-bold tracking-[0.16em] uppercase text-emerald-700 mb-3">
                  Zahlung erfolgreich
                </p>
                <h1 className="svh-h2 mb-4">Ihr Gutschein wurde erstellt</h1>
                <p className="svh-body">Vielen Dank für Ihren Kauf.</p>
              </div>

              {gutscheinCode && (
                <div className="mb-4">
                  <label className="svh-label block mb-2">Ihr Gutschein-Code</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={gutscheinCode}
                      readOnly
                      className="w-full sm:flex-1 min-w-0 px-2 py-3 text-[13px] sm:text-[17px] font-mono text-center rounded-[10px]"
                      style={{
                        background: 'var(--svh-surface-sub)',
                        border: '1px solid var(--svh-border-mid)',
                        color: 'var(--svh-ink)',
                      }}
                    />
                    <button
                      onClick={handleCopyCode}
                      className="svh-btn svh-btn--secondary svh-focus w-full sm:w-auto flex-shrink-0 px-4"
                    >
                      {copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5" />}
                      <span className="sm:hidden">Code kopieren</span>
                    </button>
                  </div>
                  {copied && <p className="svh-meta mt-2 text-emerald-700">Code kopiert.</p>}
                </div>
              )}

              <div
                className="rounded-[10px] px-5 py-4 mb-4"
                style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
              >
                <p className="svh-label mb-0.5">Wert</p>
                <p className="text-[22px] font-bold svh-num" style={{ color: 'var(--svh-ink)' }}>
                  {paymentStatus.amount_eur}€
                </p>
              </div>

              <div
                className="rounded-[10px] overflow-hidden mb-6"
                style={{ border: '1px solid var(--svh-border)' }}
              >
                <div className="px-5 py-4" style={{ background: 'var(--svh-surface-sub)' }}>
                  <p className="text-[14px] leading-relaxed break-words" style={{ color: 'var(--svh-ink-2)' }}>
                    Eine Bestätigungs-E-Mail wurde an {paymentStatus.metadata?.buyer_name as string || 'Sie'} gesendet.
                  </p>
                </div>
                <div className="flex items-start gap-3 px-5 py-4 bg-amber-50/70 border-t" style={{ borderColor: 'var(--svh-border)' }}>
                  <span className="text-[15px] flex-shrink-0" role="img" aria-label="Spam">📬</span>
                  <p className="text-[13px] leading-relaxed text-amber-900">
                    <span className="font-semibold">Keine E-Mail erhalten?</span> Bitte auch den{' '}
                    <span className="font-semibold">Spam- oder Junk-Ordner</span> prüfen.
                  </p>
                </div>
              </div>

              <hr className="svh-rule mb-6" />

              <button
                onClick={() => window.location.href = '/'}
                className="svh-btn svh-btn--primary svh-focus w-full"
              >
                Zur Startseite
              </button>
            </div>
          </div>

          <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
        </div>
      </div>
    );
  }

  // ── Court booking via Stripe ────────────────────────────────────────────────
  if (paymentStatus?.status === 'succeeded' && paymentStatus.reference_type === 'booking') {
    const meta = paymentStatus.metadata;
    const bookingConfirmedFlag = meta?.bookingConfirmed;
    const storedResult = meta?.bookingResult as BookingResultResponse | undefined;

    // Explicit conflict signalled by metadata
    if (bookingConfirmedFlag === false) {
      return (
        <BookingResultView
          result={normalizeBookingResult(
            storedResult ?? { success: false, bookingConfirmed: false },
            {
              paymentCharged: true,
              paymentRefunded: meta?.paymentRefunded === true,
            }
          )}
          autoRedirect={false}
          redirectUrl="/"
          onNavigateBooking={() => { window.location.href = '/buchen'; }}
        />
      );
    }

    // Normal success
    const successResult: BookingResultResponse = storedResult ?? {
      success: true,
      bookingConfirmed: true,
      title: 'Buchung erfolgreich',
      message: `Ihre Zahlung war erfolgreich und Ihre Buchung wurde bestätigt.${paymentStatus.amount_eur ? ` Betrag: ${paymentStatus.amount_eur}€` : ''}`,
    };
    return (
      <BookingResultView
        result={successResult}
        autoRedirect={true}
        redirectUrl="/"
        onNavigateBooking={() => { window.location.href = '/buchen'; }}
      />
    );
  }

  if (paymentStatus?.status === 'failed') {
    return (
      <BookingResultView
        result={normalizeBookingResult(
          { success: false, bookingConfirmed: false },
          { paymentCharged: true, paymentRefunded: false }
        )}
        autoRedirect={false}
        redirectUrl="/"
        onNavigateBooking={() => { window.location.href = '/buchen'; }}
      />
    );
  }

  return null;
}
